import { type CalendarDate, parseDate } from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { Download } from "lucide-react";
import z from "zod";

import { isAdminRole } from "@/auth/shared";
import { Button } from "@/components/base/button";
import { DatePicker } from "@/components/base/date-picker";
import { title } from "@/components/base/primitives";
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@/components/base/table";
import { Tab, TabList, TabPanel, Tabs } from "@/components/base/tabs";
import { SanctionBadge } from "@/components/tournaments/sanction-badge";
import type { Gender } from "@/db/schema/shared";
import {
  getTournamentDisplay,
  getTournamentDivisionDisplay,
} from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { getSanctionLabel } from "@/lib/ratings/sanction";

const currentYear = new Date().getFullYear();
const defaultStartDate = `${currentYear}-01-01`;
const defaultEndDate = new Date().toISOString().slice(0, 10);

const searchSchema = z.object({
  startDate: z.string().optional().default(defaultStartDate),
  endDate: z.string().optional().default(defaultEndDate),
  reportType: z
    .enum(["non-sanctioned", "cancelled"])
    .optional()
    .default("non-sanctioned"),
});

export const Route = createFileRoute("/admin/reports/divisions")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [{ title: "CBVA - Division Report" }],
  }),
  loader: async ({ context }) => {
    const viewer = context.viewer;
    if (!viewer || !isAdminRole(viewer.role)) {
      throw redirect({ to: "/not-found" });
    }
  },
  component: RouteComponent,
});

type ReportType = "non-sanctioned" | "cancelled";

type ReportRow = {
  tournamentDivisionId: number;
  tournament: {
    id: number;
    name: string | null;
    date: string;
    venue: { name: string; city: string; slug: string };
  };
  division: { name: string; maxAge: number | null; display: string | null };
  tournamentDivision: {
    name: string | null;
    gender: Gender;
    teamSize: number;
    status: string;
    displayGender: boolean | null;
    displayDivision: boolean | null;
  };
  teamCount: number;
  sanctionStatus:
    | { sanctioned: false }
    | { sanctioned: true; sanctionedAs: string };
};

function csvCell(value: string) {
  return `"${value.replace(/"/g, '""')}"`;
}

function downloadCsv(rows: ReportRow[]) {
  const header = "Tournament,Date,Location,Division,Teams,Status,Sanction";
  const csvRows = rows.map((row) => {
    const divDisplay = getTournamentDivisionDisplay({
      ...row.tournamentDivision,
      division: row.division,
    });
    const sanction =
      row.tournamentDivision.status === "cancelled"
        ? "Cancelled"
        : getSanctionLabel(row.sanctionStatus, row.division.name);
    const { venue } = row.tournament;
    return [
      csvCell(row.tournament.name ?? ""),
      row.tournament.date,
      csvCell(`${venue.name}, ${venue.city}`),
      csvCell(divDisplay),
      row.teamCount,
      row.tournamentDivision.status,
      sanction,
    ].join(",");
  });

  const blob = new Blob([header + "\n" + csvRows.join("\n")], {
    type: "text/csv",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "division-report.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function RouteComponent() {
  const { startDate, endDate, reportType } = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/reports/divisions" });
  const trpc = useTRPC();

  const { data: rows, isLoading } = useQuery(
    trpc.tournaments.divisionReport.queryOptions({
      startDate,
      endDate,
      reportType,
    }),
  );

  return (
    <DefaultLayout
      classNames={{
        content: "flex flex-col space-y-8 w-full max-w-4xl px-3 py-12 mx-auto",
      }}
    >
      <section className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <h2 className={title({ size: "sm" })}>Division Report</h2>
          {rows && rows.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onPress={() => downloadCsv(rows as ReportRow[])}
            >
              <Download className="mr-1 h-4 w-4" />
              Download CSV
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <DatePicker<CalendarDate>
            label="Start Date"
            value={startDate ? parseDate(startDate) : null}
            onChange={(value) => {
              navigate({
                replace: true,
                search: (search) => ({
                  ...search,
                  startDate: value?.toString() || undefined,
                }),
              });
            }}
          />
          <DatePicker<CalendarDate>
            label="End Date"
            value={endDate ? parseDate(endDate) : null}
            onChange={(value) => {
              navigate({
                replace: true,
                search: (search) => ({
                  ...search,
                  endDate: value?.toString() || undefined,
                }),
              });
            }}
          />
        </div>

        <Tabs
          selectedKey={reportType}
          onSelectionChange={(key) => {
            navigate({
              replace: true,
              search: (search) => ({
                ...search,
                reportType: key as ReportType,
              }),
            });
          }}
        >
          <TabList>
            <Tab id="non-sanctioned">Non-Sanctioned</Tab>
            <Tab id="cancelled">Cancelled</Tab>
          </TabList>
          <TabPanel id="non-sanctioned">
            <ReportTable
              rows={rows as ReportRow[] | undefined}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel id="cancelled">
            <ReportTable
              rows={rows as ReportRow[] | undefined}
              isLoading={isLoading}
            />
          </TabPanel>
        </Tabs>
      </section>
    </DefaultLayout>
  );
}

function ReportTable({
  rows,
  isLoading,
}: {
  rows: ReportRow[] | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <p className="py-4 text-gray-500">Loading...</p>;
  }

  if (!rows || rows.length === 0) {
    return <p className="py-4 text-gray-500">No divisions found.</p>;
  }

  return (
    <Table aria-label="Division report">
      <TableHeader>
        <TableColumn isRowHeader>Tournament</TableColumn>
        <TableColumn>Division</TableColumn>
        <TableColumn>Teams</TableColumn>
        <TableColumn>Status / Sanction</TableColumn>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.tournamentDivisionId}>
            <TableCell>
              <Link
                to="/tournaments/$tournamentId"
                params={{ tournamentId: String(row.tournament.id) }}
                className="text-blue-600 hover:underline"
              >
                {getTournamentDisplay(row.tournament, true)}
              </Link>
            </TableCell>
            <TableCell>
              <Link
                to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                params={{
                  tournamentId: String(row.tournament.id),
                  divisionId: String(row.tournamentDivisionId),
                }}
                className="text-blue-600 hover:underline"
              >
                {getTournamentDivisionDisplay({
                  ...row.tournamentDivision,
                  division: row.division,
                })}
              </Link>
            </TableCell>
            <TableCell>{row.teamCount}</TableCell>
            <TableCell>
              {row.tournamentDivision.status === "cancelled" ? (
                <span className="text-red-600">Cancelled</span>
              ) : (
                <SanctionBadge
                  sanction={row.sanctionStatus}
                  divisionName={row.division.name}
                  showDowngradedLevel
                />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
